import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { slugify } from "./shipRefUtils";

// RC8 · Single source of truth for reading the provider-backed ship-reference
// read model (/api/ships/{id}/cabins|dining|venues). Never CKB, never raw
// staging. Shared by the compact "Explore ship" module (Voyage Home) and the
// dedicated Ship Guide so both issue the same requests and honour identical
// entitlement / empty / error semantics.
//
//   denied  → HTTP 401/403 (no licensed entitlement). Consumers must not leak.
//   error   → 5xx / network. Recoverable — expose Retry, never hide silently.
//   empty   → licensed but read model absent (source "none") → "not loaded yet".

const ENDPOINTS = [
  { key: "cabins", endpoint: "cabins", listKey: "categories" },
  { key: "dining", endpoint: "dining", listKey: "dining" },
  { key: "venues", endpoint: "venues", listKey: "venues" },
  { key: "decks", endpoint: "decks", listKey: "decks" },
];

const EMPTY = { cabins: [], dining: [], venues: [], decks: [] };

export function useShipReference(shipId, shipName) {
  const sid = shipId || slugify(shipName);
  const [state, setState] = useState({
    loading: true, error: false, denied: false, ...EMPTY, meta: {},
  });

  const load = useCallback(async () => {
    if (!sid) {
      setState({ loading: false, error: false, denied: false, ...EMPTY, meta: {} });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: false, denied: false }));
    try {
      const results = await Promise.all(
        ENDPOINTS.map((e) => api.get(`/api/ships/${sid}/${e.endpoint}`).then((r) => r.data)),
      );
      const next = { ...EMPTY };
      let meta = {};
      ENDPOINTS.forEach((e, i) => {
        const d = results[i] || {};
        next[e.key] = Array.isArray(d[e.listKey]) ? d[e.listKey] : [];
        if (d.source === "widgety") {
          meta = { source: "widgety", updated_at: d.updated_at, degraded: !!d.degraded_source,
                   quality: d.quality || null, ship_name: d.ship_name };
        }
      });
      setState({ loading: false, error: false, denied: false, ...next, meta });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setState({ loading: false, error: false, denied: true, ...EMPTY, meta: {} });
        return;
      }
      setState({ loading: false, error: true, denied: false, ...EMPTY, meta: {} });
    }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  return { sid, ...state, reload: load };
}

// Read the traveler's canonical booked dining reservations. These are voyage /
// booking records — NOT Widgety reference data. Degrades to an empty list on
// any error so a missing capability never blocks the surface.
export function useBookedDining() {
  const [state, setState] = useState({ loading: true, reservations: [] });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/cruises/current/dining-reservations");
        const rows = Array.isArray(r.data?.reservations) ? r.data.reservations : [];
        if (alive) setState({ loading: false, reservations: rows });
      } catch {
        if (alive) setState({ loading: false, reservations: [] });
      }
    })();
    return () => { alive = false; };
  }, []);
  return state;
}

// Read the traveler's canonical booked ENTERTAINMENT reservations from the
// authoritative experience-events model (kind=entertainment). NOT Widgety
// data. Degrades to an empty list on any error so the surface never blocks.
export function useBookedEntertainment() {
  const [state, setState] = useState({ loading: true, events: [] });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/api/cruises/current/experience-events?kind=entertainment");
        const rows = Array.isArray(r.data?.events) ? r.data.events : [];
        if (alive) setState({ loading: false, events: rows });
      } catch {
        if (alive) setState({ loading: false, events: [] });
      }
    })();
    return () => { alive = false; };
  }, []);
  return state;
}
